import type { ReactNode } from "react";
import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FileMinus,
  FileText,
  HelpCircle,
  History,
  LayoutDashboard,
  Link2,
  Receipt,
  Wallet,
} from "lucide-react";
import { DOWNLOAD_PAGES, ROUTES } from "@/config/routes";
import { PLAN_NAV_LABEL } from "@/features/billing/planCopy";
import { BrandLogo } from "@/features/layout/components/BrandLogo";
import { getAccountId } from "@/lib/auth/account";
import {
  connectedAccountTitle,
  formatConnectedAccount,
} from "@/lib/auth/formatConnectedAccount";
import { logout } from "@/lib/auth/logout";
import { markHadSession } from "@/lib/auth/sessionMarker";
import { cn } from "@/utils/cn";

function navItemClass({ isActive }: { isActive: boolean }) {
  return cn(
    "flex items-center justify-between gap-2 rounded-[var(--radius-sm)] px-2.5 py-2 text-[0.84rem] font-medium leading-snug",
    isActive
      ? "bg-[var(--accent-light)] font-semibold text-[var(--accent)]"
      : "text-[var(--muted)] hover:bg-white/60 hover:text-[var(--text)]",
  );
}

function NavGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-3 first:mt-2">
      <div className="px-2.5 pb-0.5 text-[0.68rem] font-bold uppercase tracking-wider text-[var(--muted)]">
        {label}
      </div>
      <div className="ml-2 flex flex-col gap-0.5 border-l-2 border-[var(--border-soft)] pl-3">
        {children}
      </div>
    </div>
  );
}

function NavItemLabel({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden className="text-[var(--muted)]">
        {icon}
      </span>
      <span>{label}</span>
    </span>
  );
}

export function Sidebar() {
  const accountId = getAccountId();
  const [signingOut, setSigningOut] = useState(false);

  const onSignOut = () => {
    setSigningOut(true);
    markHadSession();
    void logout().finally(() => {
      window.location.assign(ROUTES.sessionExpired);
    });
  };

  return (
    <aside className="flex h-full min-h-0 w-[var(--sidebar-w)] shrink-0 flex-col overflow-y-auto border-r border-[var(--border)] bg-[var(--surface-2)] py-5">
      <BrandLogo className="px-3 pb-5 text-[0.95rem]" iconClassName="h-7 w-7" />

      <nav className="flex flex-col gap-0.5 px-3">
        <NavLink to={ROUTES.dashboard} end className={navItemClass}>
          <NavItemLabel icon={<LayoutDashboard size={16} />} label="Dashboard" />
        </NavLink>

        <NavLink to={ROUTES.exports} className={navItemClass}>
          <NavItemLabel icon={<History size={16} />} label="Export History" />
        </NavLink>

        <NavGroup label="Download">
          {DOWNLOAD_PAGES.map(({ route, label }) => {
            const icon =
              route === ROUTES.invoicesReceipts
                ? <Receipt size={16} />
                : route === ROUTES.invoicesCreditNotes
                  ? <FileMinus size={16} />
                  : route === ROUTES.invoicesPayLinks
                    ? <Link2 size={16} />
                    : <FileText size={16} />;
            return (
            <NavLink key={route} to={route} end className={navItemClass}>
              <NavItemLabel icon={icon} label={label} />
            </NavLink>
            );
          })}
        </NavGroup>

        <NavLink
          to={ROUTES.billing}
          className={(props) => cn(navItemClass(props), "mt-3")}
        >
          <NavItemLabel icon={<Wallet size={16} />} label={PLAN_NAV_LABEL} />
        </NavLink>
      </nav>

      <div className="mt-auto px-3 pt-6">
        <NavLink to={ROUTES.help} className={navItemClass}>
          <NavItemLabel icon={<HelpCircle size={16} />} label="Help & Support" />
        </NavLink>
        <div className="mt-3 flex flex-col gap-0.5 px-2.5 text-[0.75rem]">
          <span className="text-[var(--muted)]">Connected</span>
          <strong
            className="break-all font-mono font-normal text-[var(--text)]"
            title={connectedAccountTitle(accountId)}
          >
            {formatConnectedAccount(accountId)}
          </strong>
        </div>
        {accountId?.startsWith("acct_") ? (
          <button
            type="button"
            className="mt-2 w-full rounded-[var(--radius-sm)] px-2.5 py-1.5 text-left text-[0.78rem] font-medium text-[var(--muted)] hover:bg-white/60 hover:text-[var(--text)]"
            disabled={signingOut}
            onClick={onSignOut}
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
